import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Loader } from 'ui-kit';
import { BUTTON_TYPES } from '_constants';
import * as Styled from '../styles';

// Generic Yes / Cancel confirmation modal for destructive or irreversible
// actions. The caller passes a title, an optional list of consequences, and
// the async confirm handler. The modal shows a loader while the handler is
// in flight and closes itself on success.
function ConfirmActionModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  consequences,
  confirmText,
  cancelText,
  confirmVariant,
}) {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      // Don't swallow it silently — a half-applied confirm is otherwise invisible.
      // eslint-disable-next-line no-console
      console.error(err);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal open={isOpen} onClose={loading ? () => {} : onClose} autoHeight>
      {loading && <Loader />}
      <Styled.CompactModalWrapper>
        <Styled.ModalTitle>{title}</Styled.ModalTitle>
        {description && (
          <Styled.ModalDescription as="p" style={{ listStyle: 'none', padding: 0 }}>
            {description}
          </Styled.ModalDescription>
        )}
        {consequences && consequences.length > 0 && (
          <Styled.ModalDescription>
            {consequences.map((tip) => (
              <Styled.ModalTips key={tip}>{tip}</Styled.ModalTips>
            ))}
          </Styled.ModalDescription>
        )}
        <Styled.ModalButtonWrapper>
          <Styled.ModalButtonCancel
            onClick={onClose}
            text={cancelText}
            variant={BUTTON_TYPES.DARK}
            $variantColors
            aria-label={cancelText}
            disabled={loading}
          />
          <Styled.ModalButtonSet
            onClick={handleConfirm}
            text={confirmText}
            variant={confirmVariant}
            $variantColors
            aria-label={confirmText}
            disabled={loading}
          />
        </Styled.ModalButtonWrapper>
      </Styled.CompactModalWrapper>
    </Modal>
  );
}

ConfirmActionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  consequences: PropTypes.arrayOf(PropTypes.string),
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  confirmVariant: PropTypes.string,
};

ConfirmActionModal.defaultProps = {
  description: '',
  consequences: [],
  confirmText: 'Yes, I agree',
  cancelText: 'Cancel',
  confirmVariant: BUTTON_TYPES.DANGER,
};

export default ConfirmActionModal;
